import { HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import { version as cloudxrSdkVersion } from "@nvidia/cloudxr/package.json";
import * as Sentry from "@sentry/react";
import { backOff } from "exponential-backoff";
import log from "loglevel";
import { cloudRenderingSessionManagementServiceUrl } from "./config";
import {
  clearDevices,
  updateDevices,
  updateInstalledApps,
} from "./features/devicesSlice";
import {
  appInstalled,
  appLaunched,
  issueHandled,
  notification,
  reportIssue,
  selectSessionState,
  sessionCreated,
  sessionError,
  sessionOverview,
  sessionProgress,
  sessionRequested,
  sessionTerminated,
  setConnectionState,
  setSessionState,
} from "./features/sessionSlice";
import { ApplicationBuildId } from "./hooks/types";
import { SESSION_STATE } from "./session/session-state";
import {
  AppInstalledMessage,
  AppLaunchedMessage,
  ClientIdentificationListMessage,
  ClientIdentificationMessage,
  CloudRenderedSessionCreatedMessage,
  DeviceType,
  ErrorMessage,
  InstalledAppsMessage,
  IssueHandledMessage,
  NotificationMessage,
  ReportedIssue,
  RequestSessionMessage,
  SessionCreatedMessage,
  SessionOverviewMessage,
  SessionStartupProgressMessage,
  SessionType,
} from "./session/types";
import { store } from "./store";
import { getUniqueIdForApplication } from "./utils/unique-ids";
import { version } from "./utils/version";
import {
  isPicoBrowserUa,
  isQuestBrowserUa,
  parsePicoModel,
  parseQuestModel,
  isEmulatedHeadsetOnDesktop,
} from "./utils/vrDevice";

/**
 * Identifies the browser to Session Management. Portal-Frontend is meant to
 * run inside a VR device's web browser (Quest, Pico) where the browser IS
 * the headset; on a PC browser during development it's just a browser.
 * Detect that from the User-Agent.
 *
 * `xrStreamingProtocol` (CXR<version>) is announced only for XR clients — real
 * Quest/Pico headsets, or a desktop browser spoofed as a headset by the Meta
 * Immersive Web Emulator (matched by the same UA checks). It's the signal
 * Session Management uses to provision CloudXR endpoints
 * (`Session-Management/Application/Services/SessionsService.cs:154`).
 * A plain desktop browser identifies as a regular `Browser` with no protocol.
 */
const CLOUDXR_PROTOCOL = `CXR${cloudxrSdkVersion}`;

type DetectedClientEnvironment = Pick<
  ClientIdentificationMessage,
  | "name"
  | "type"
  | "version"
  | "vendorName"
  | "modelName"
  | "xrStreamingProtocol"
>;

function detectClientEnvironment(): DetectedClientEnvironment {
  // `compare-versions` (used on the Standalone identification path) rejects a
  // non-semver `version` ("dev" in local builds), so fall back to a fixed
  // semver only then — real production builds report their true version.
  const standaloneVersion = /^\d+\.\d+\.\d+/.test(version) ? version : "1.0.0";
  if (typeof window !== "undefined") {
    const ua = window.navigator.userAgent;

    // Pico MUST be checked before Oculus: the Pico browser's User-Agent
    // contains BOTH "PicoBrowser" and "OculusBrowser" (it spoofs Oculus), so an
    // Oculus-first check would misidentify every Pico as a Quest. The shared
    // UA helpers live in utils/vrDevice. isEmulatedHeadsetOnDesktop labels a
    // desktop browser running the Meta emulator distinctly (display-only — the
    // functional identity stays a headset so CloudXR still works).
    if (isPicoBrowserUa(ua)) {
      const model = parsePicoModel(ua);
      return {
        name: isEmulatedHeadsetOnDesktop() ? `${model} (Emulator)` : model,
        type: DeviceType.Standalone,
        vendorName: "pico",
        modelName: model.toLowerCase(),
        version: standaloneVersion,
        xrStreamingProtocol: CLOUDXR_PROTOCOL,
      };
    }
    if (isQuestBrowserUa(ua)) {
      const model = parseQuestModel(ua);
      return {
        name: isEmulatedHeadsetOnDesktop() ? `${model} (Emulator)` : model,
        type: DeviceType.Standalone,
        vendorName: "meta",
        modelName: model.toLowerCase(),
        version: standaloneVersion,
        xrStreamingProtocol: CLOUDXR_PROTOCOL,
      };
    }
  }

  // A plain desktop browser (no headset UA, real or emulated): a regular
  // Browser with no xrStreamingProtocol, so it never appears in the VR device
  // picker and Session Management does not provision CloudXR. To use CloudXR on
  // desktop for testing, install the Meta Immersive Web Emulator extension — it
  // spoofs a Quest UA (matched by the Oculus branch above) and provides WebXR.
  return {
    name: "Portal (Web)",
    type: DeviceType.Browser,
    version,
  };
}

function buildClientIdentification(): ClientIdentificationMessage {
  return {
    identifier: getUniqueIdForApplication(),
    ...detectClientEnvironment(),
  };
}

function getConnectionString(clientIdentification: ClientIdentificationMessage) {
  const baseUrl = `${cloudRenderingSessionManagementServiceUrl}/hub/sessions`;

  const identificationQueryString = new URLSearchParams(
    // convert an object to a dictionary
    JSON.parse(JSON.stringify(clientIdentification)),
  );

  const url = `${baseUrl}?${identificationQueryString.toString()}`;
  return url;
}

let token: string | undefined = undefined;

export function setToken(newToken: string) {
  token = newToken;

  if (_connection) return;

  createSignalRConnection();
}

let _connection: HubConnection;

function createSignalRConnection() {
  // Built once per connection (after auth), reflecting the current device /
  // User-Agent (real headset, emulated headset, or plain browser).
  const clientIdentification = buildClientIdentification();

  _connection = new HubConnectionBuilder()
    .withUrl(getConnectionString(clientIdentification), {
      accessTokenFactory: () => token ?? "",
      withCredentials: true,
    })
    .build();

  // add any listeners
  // Provide information about this application upon request
  _connection.on("RequestIdentification", () => {
    _connection
      .invoke("ClientIdentification", clientIdentification)
      .catch((err) => log.error(err));
  });
  // remember any connected clients
  _connection.on(
    "ClientIdentificationList",
    (data: ClientIdentificationListMessage) =>
      store.dispatch(updateDevices(data)),
  );
  _connection.on("InstalledApps", (data: InstalledAppsMessage) => {
    store.dispatch(updateInstalledApps(data));
  });
  _connection.on("SessionOverview", (data: SessionOverviewMessage) => {
    log.debug("Existing Session received", data);
    store.dispatch(sessionOverview(data));
  });
  _connection.on(
    "SessionCreated",
    (data: SessionCreatedMessage | CloudRenderedSessionCreatedMessage) => {
      log.debug("Session Created", data);
      store.dispatch(sessionCreated(data));
    },
  );

  _connection.on("SessionTerminated", (data: { id: string }) => {
    log.debug("Session Terminated", data);
    store.dispatch(sessionTerminated(data));
  });

  _connection.on("StartupProgress", (data: SessionStartupProgressMessage) => {
    log.debug("Startup Progress", data);
    store.dispatch(sessionProgress(data));
  });

  _connection.on("AppLaunched", (data: AppLaunchedMessage) => {
    log.debug("App launched", data.sessionId, data.deviceIdentifier);
    store.dispatch(appLaunched(data));
  });

  _connection.on("AppInstalled", (data: AppInstalledMessage) => {
    log.debug("Connect Command", data);
    store.dispatch(appInstalled(data));
  });

  _connection.on("ReportIssue", (data: ReportedIssue) => {
    log.debug("ReportIssue Command", data);
    store.dispatch(reportIssue(data));
  });

  _connection.on("IssueHandled", (data: IssueHandledMessage) => {
    log.debug("Issue Handled Command", data);
    store.dispatch(issueHandled(data));
  });

  _connection.on("SessionError", (data: ErrorMessage) => {
    log.debug("Session Error", data);
    store.dispatch(sessionError(data));
  });

  _connection.on("Notification", (data: NotificationMessage) => {
    log.debug("Notification", data);
    store.dispatch(notification(data));
  });

  // handle any changes in the connection state (e.g. dropped connection)
  const handleConnectionChange = () => {
    if (_connection) store.dispatch(setConnectionState(_connection.state));
    // without connection, no devices or session
    store.dispatch(clearDevices());
  };
  _connection.onreconnecting(handleConnectionChange);
  _connection.onclose(() => {
    handleConnectionChange();
    connectWithBackoff();
  });

  _connection.onreconnected(() => {
    if (_connection) {
      store.dispatch(setConnectionState(_connection.state));
    }
  });

  // establish the connection (and retry with exponential backoff if it fails)
  const connectWithBackoff = () => {
    backOff(
      () => {
        if (!token) throw new Error("token not set");

        return _connection.start();
      },
      {
        maxDelay: 10000,
        numOfAttempts: Infinity,
      },
    )
      .catch(() => {
        const error = new Error(
          `Could not connect to Session Management at ${_connection.baseUrl}`,
        );
        log.error(error);
        Sentry.captureException(error);
      })
      .finally(() => store.dispatch(setConnectionState(_connection.state)));
  };

  connectWithBackoff();
}

export async function terminateSession(sessionId: string | undefined) {
  if (!sessionId) {
    Sentry.captureException(
      new Error(
        "Attempted to terminate session without having an active session",
      ),
    );
    return;
  }
  try {
    store.dispatch(setSessionState(SESSION_STATE.ENDING));
    if (_connection)
      await _connection.invoke("TerminateSession", {
        Id: sessionId,
      });
  } catch (err) {
    store.dispatch(
      sessionError({
        message: "Error while attempting to terminate session",
        sessionId: sessionId,
      }),
    );

    log.debug("Error while attempting to terminate session", err);
    Sentry.captureException(err);
  }
}

type SessionRequestArgs = {
  appBuildId: ApplicationBuildId;
  type: SessionType;
  deviceIdentifier: string;
  launchArguments: string;
  organizationId: string | number;
  renderRegion?: string;
  vmSize?: string;
  forceColdVm?: boolean;
  encryptVrStream?: boolean;
  withXrGateway?: boolean;
  vmImage?: string;
  virtualMachineId?: string;
  debugModeEnabled?: boolean;
  withStreamingGateway?: boolean;
  // Location data for server-side region selection / failover when no
  // renderRegion is pinned (automatic mode). See RequestSessionMessage.
  position?: { latitude: number; longitude: number };
  regionLatencyMs?: { [region: string]: number };
};

export async function requestSession({
  appBuildId,
  type,
  deviceIdentifier,
  launchArguments,
  organizationId,
  renderRegion,
  vmSize,
  forceColdVm = false,
  encryptVrStream,
  withXrGateway = false,
  vmImage,
  virtualMachineId,
  debugModeEnabled = false,
  withStreamingGateway = false,
  position,
  regionLatencyMs,
}: SessionRequestArgs) {
  const isCloudSession =
    type === SessionType.CloudRenderedVR ||
    type === SessionType.CloudRenderedNonVR;

  // Location inputs are only useful when no explicit region is pinned. An empty
  // latency map (the default from the redux slice) carries no information, so
  // treat it as absent rather than as valid location data.
  const hasMeasuredLatency =
    !!regionLatencyMs && Object.keys(regionLatencyMs).length > 0;
  const hasLocationData = !!position || hasMeasuredLatency;

  if (!renderRegion && !hasLocationData && isCloudSession) {
    // A cloud session needs either an explicit region or location data so that
    // Session Management can select one itself.
    log.error(
      "Cannot launch a cloud rendered session without a region or location data for server-side region selection",
    );
    return;
  }

  const requestSessionArgs: RequestSessionMessage = {
    appBuildId: appBuildId.toString(),
    deviceIdentifier,
    extraLaunchArguments: launchArguments,
    organizationId: organizationId.toString(),
    renderRegion,
    vmSize: vmSize,
    vmImage: vmImage,
    forceColdVm: forceColdVm,
    xrEncryption: encryptVrStream,
    requireXRGateway: withXrGateway,
    debugModeEnabled,
    virtualMachineId,
    requireDCV: withStreamingGateway,
    // Session Management rejects position/latency when an explicit region is
    // set, so only forward location data for automatic region selection.
    position: renderRegion ? undefined : position,
    regionLatencyMs:
      renderRegion || !hasMeasuredLatency ? undefined : regionLatencyMs,
  };
  const currentSession = selectSessionState(store.getState());
  const isSessionWithSameConfiguration = (
    Object.keys(requestSessionArgs) as (keyof RequestSessionMessage)[]
  ).every((key) => {
    if (
      key === "forceColdVm" ||
      key === "virtualMachineId" ||
      key === "requireDCV" ||
      key === "requireXRGateway"
    ) {
      return !requestSessionArgs[key];
    } else if (key === "position" || key === "regionLatencyMs") {
      // location data only informs server-side region selection and does not
      // change the session's identity (these are objects, so a reference
      // comparison would otherwise always report a different configuration)
      return true;
    } else if (key === "appBuildId") {
      return (
        currentSession.applicationBuildId ===
        Number(requestSessionArgs.appBuildId)
      );
    }

    return currentSession[key] === requestSessionArgs[key];
  });
  if (
    isSessionWithSameConfiguration &&
    currentSession.state < SESSION_STATE.ENDED
  ) {
    log.debug("Session with same parameters already running");
    return;
  }

  try {
    store.dispatch(sessionRequested({ ...requestSessionArgs, type }));
    await _connection?.invoke("RequestSession", requestSessionArgs);
  } catch (err) {
    store.dispatch(sessionError({ message: (err as Error).message }));
    log.error("Error while attempting to create a new session", err);
    Sentry.captureException(err);
  }
}

export async function sendSessionActivity(sessionId: string) {
  try {
    await _connection?.invoke("SessionActivity", {
      SessionId: sessionId,
    });
  } catch (err) {
    log.error("Failed", err);
  }
}

/**
 * Tells Session Management to launch the application on the VM. Real client
 * devices (AVP, Quest) invoke this once they've connected to the render
 * server; the CloudXR streamer mirrors that behavior so Session Management
 * proceeds to AppLaunched and the session reaches ACTIVE.
 */
export async function launchApp(sessionId: string) {
  if (!_connection) {
    log.warn("LaunchApp skipped: no SignalR connection", { sessionId });
    return;
  }
  if (_connection.state !== "Connected") {
    log.warn("LaunchApp skipped: SignalR not connected", {
      sessionId,
      state: _connection.state,
    });
    return;
  }
  try {
    log.info("LaunchApp invoking", { sessionId });
    await _connection.invoke("LaunchApp", { SessionId: sessionId });
    log.info("LaunchApp invoked successfully", { sessionId });
  } catch (err) {
    log.error("LaunchApp invoke failed", err);
    Sentry.captureException(err);
  }
}

export async function sendEnableDebug(sessionId: string) {
  try {
    await _connection?.invoke("EnableDebugMode", {
      SessionId: sessionId,
    });
    return true;
  } catch (err) {
    log.error("Failed", err);
    return false;
  }
}

export async function sendIssueHandled(handledIssueInfo: IssueHandledMessage) {
  try {
    await _connection?.invoke("IssueHandled", handledIssueInfo);
  } catch (err) {
    log.error("Failed", err);
  }
}

export async function sendIssueReport(
  issueInfo: Omit<ReportedIssue, "sourceDeviceIdentifier">,
) {
  try {
    await _connection?.invoke("ReportIssue", issueInfo);
  } catch (err) {
    log.error("Failed", err);
  }
}
